/**
 * Task Filters Component
 *
 * Provides search and filter controls for the scheduled tasks list:
 * - Text search by task name
 * - Status filter (All/Enabled/Disabled)
 * - Sort options (Name/Next Run/Last Run/Created)
 */

import React from 'react';
import { Input } from '../ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Search } from 'lucide-react';

export type TaskStatusFilter = 'all' | 'enabled' | 'disabled';
export type TaskSortOption = 'name' | 'nextRun' | 'lastRun' | 'created';

export interface TaskFiltersState {
  search: string;
  status: TaskStatusFilter;
  sort: TaskSortOption;
}

interface TaskFiltersProps {
  filters: TaskFiltersState;
  onFiltersChange: (filters: TaskFiltersState) => void;
  taskCount: number;
  filteredCount: number;
}

export function TaskFilters({ filters, onFiltersChange, taskCount, filteredCount }: TaskFiltersProps) {
  const handleSearchChange = (value: string) => {
    onFiltersChange({ ...filters, search: value });
  };

  const handleStatusChange = (value: TaskStatusFilter) => {
    onFiltersChange({ ...filters, status: value });
  };

  const handleSortChange = (value: TaskSortOption) => {
    onFiltersChange({ ...filters, sort: value });
  };

  const showingFiltered = filteredCount !== taskCount;

  return (
    <div className="flex flex-col gap-3 mb-4">
      {/* Search and Filters Row */}
      <div className="flex flex-col sm:flex-row gap-3">
        {/* Search Input */}
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            type="text"
            placeholder="Search tasks..."
            value={filters.search}
            onChange={(e) => handleSearchChange(e.target.value)}
            className="pl-9"
          />
        </div>

        {/* Status Filter */}
        <div className="flex items-center gap-2">
          <span className="text-sm text-muted-foreground whitespace-nowrap">Status:</span>
          <Select value={filters.status} onValueChange={handleStatusChange}>
            <SelectTrigger className="w-[120px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All</SelectItem>
              <SelectItem value="enabled">Enabled</SelectItem>
              <SelectItem value="disabled">Disabled</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Sort Option */}
        <div className="flex items-center gap-2">
          <span className="text-sm text-muted-foreground whitespace-nowrap">Sort:</span>
          <Select value={filters.sort} onValueChange={handleSortChange}>
            <SelectTrigger className="w-[130px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="name">Name</SelectItem>
              <SelectItem value="nextRun">Next Run</SelectItem>
              <SelectItem value="lastRun">Last Run</SelectItem>
              <SelectItem value="created">Created</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Results Count */}
      {showingFiltered && (
        <div className="text-sm text-muted-foreground">
          Showing {filteredCount} of {taskCount} tasks
        </div>
      )}
    </div>
  );
}

/**
 * Default filter state
 */
export const DEFAULT_FILTERS: TaskFiltersState = {
  search: '',
  status: 'all',
  sort: 'name'
};

/**
 * Load filters from localStorage
 */
export function loadFiltersFromStorage(): TaskFiltersState {
  try {
    const stored = localStorage.getItem('scheduledTaskFilters');
    if (stored) {
      const parsed = JSON.parse(stored);
      return {
        search: '', // Don't persist search text
        status: parsed.status || 'all',
        sort: parsed.sort || 'name'
      };
    }
  } catch (e) {
    console.warn('Failed to load task filters from localStorage:', e);
  }
  return DEFAULT_FILTERS;
}

/**
 * Save filters to localStorage
 */
export function saveFiltersToStorage(filters: TaskFiltersState): void {
  try {
    localStorage.setItem('scheduledTaskFilters', JSON.stringify({
      status: filters.status,
      sort: filters.sort
      // Don't persist search text
    }));
  } catch (e) {
    console.warn('Failed to save task filters to localStorage:', e);
  }
}
